/**
 * Slack service for sending notifications to healthcare team channels
 * Handles patient registration, appointment alerts, and system notifications
 */
export class SlackService {
    webhookUrl;
    unifiedWebhookUrl;
    botToken;
    defaultChannel;
    environment;
    constructor() {
        this.webhookUrl = process.env.SLACK_WEBHOOK_URL || '';
        this.unifiedWebhookUrl = process.env.SLACK_UNIFIED_WEBHOOK_URL || '';
        this.botToken = process.env.SLACK_BOT_TOKEN || '';
        this.defaultChannel = process.env.SLACK_DEFAULT_CHANNEL || '#patient-alerts';
        this.environment = process.env.VITE_ENVIRONMENT || 'local';
        if (!this.webhookUrl && !this.botToken) {
            console.warn('⚠️ Neither Slack webhook URL nor bot token configured. Notifications will be logged only.');
        }
        if (!this.unifiedWebhookUrl) {
            console.warn('⚠️ Slack unified webhook URL not configured. No unified feed will be created.');
        }
    }
    /**
     * Send notification for new user authentication (auth.users)
     */
    async notifyUserAuthentication(userData) {
        const isProduction = this.environment === 'production';
        const message = {
            text: `🔐 New User Authentication ${isProduction ? '(PRODUCTION)' : '(STAGING)'}`,
            channel: isProduction ? '#user-registrations' : '#user-registrations-staging',
            username: 'JoinOmu Auth Bot',
            icon_emoji: '🔐',
            attachments: [
                {
                    color: isProduction ? '#007bff' : '#6c757d',
                    title: `New User: ${userData.email}`,
                    fields: [
                        {
                            title: 'Email',
                            value: userData.email,
                            short: true
                        },
                        {
                            title: 'User ID',
                            value: userData.id,
                            short: true
                        },
                        {
                            title: 'Email Confirmed',
                            value: userData.email_confirmed_at ? '✅ Yes' : '❌ Pending',
                            short: true
                        },
                        {
                            title: 'Role',
                            value: userData.role || 'Not set',
                            short: true
                        },
                        {
                            title: 'Registration Time',
                            value: new Date(userData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Authentication System',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send notification for new patient registration
     */
    async notifyPatientRegistration(patientData) {
        const isProduction = this.environment === 'production';
        const message = {
            text: `🏥 New Patient Registration ${isProduction ? '(PRODUCTION)' : '(STAGING)'}`,
            channel: isProduction ? '#patient-registrations' : '#patient-registrations-staging',
            username: 'JoinOmu Bot',
            icon_emoji: '🏥',
            attachments: [
                {
                    color: isProduction ? '#28a745' : '#ffc107', // Green for production, yellow for staging
                    title: `${patientData.first_name} ${patientData.last_name}`,
                    fields: [
                        {
                            title: 'Email',
                            value: patientData.email,
                            short: true
                        },
                        {
                            title: 'Patient ID',
                            value: patientData.id,
                            short: true
                        },
                        {
                            title: 'Profile ID',
                            value: patientData.profile_id || 'Not set',
                            short: true
                        },
                        {
                            title: 'Registration Time',
                            value: new Date(patientData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Healthcare Platform',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send notification for system alerts (errors, warnings, etc.)
     */
    async notifySystemAlert(alert) {
        const colors = {
            info: '#17a2b8',
            warning: '#ffc107',
            error: '#dc3545',
            critical: '#721c24'
        };
        const emojis = {
            info: 'ℹ️',
            warning: '⚠️',
            error: '❌',
            critical: '🚨'
        };
        const message = {
            text: `${emojis[alert.level]} System Alert: ${alert.title}`,
            channel: '#system-alerts',
            username: 'JoinOmu Monitor',
            icon_emoji: emojis[alert.level],
            attachments: [
                {
                    color: colors[alert.level],
                    title: alert.title,
                    text: alert.message,
                    fields: alert.details ? [
                        {
                            title: 'Details',
                            value: '```' + JSON.stringify(alert.details, null, 2) + '```',
                            short: false
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        },
                        {
                            title: 'Timestamp',
                            value: new Date().toISOString(),
                            short: true
                        }
                    ] : [],
                    footer: 'JoinOmu System Monitor',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send notification for new appointment booking
     */
    async notifyAppointmentBooking(appointmentData) {
        const isProduction = this.environment === 'production';
        const message = {
            text: `📅 New Appointment Booked ${isProduction ? '(PRODUCTION)' : '(STAGING)'}`,
            channel: isProduction ? '#appointments' : '#appointments-staging',
            username: 'JoinOmu Appointments Bot',
            icon_emoji: '📅',
            attachments: [
                {
                    color: isProduction ? '#28a745' : '#6c757d',
                    title: `Appointment: ${appointmentData.patient_name || 'Unknown Patient'} → ${appointmentData.provider_name || 'Unknown Provider'}`,
                    fields: [
                        {
                            title: 'Patient',
                            value: appointmentData.patient_name || `ID: ${appointmentData.patient_id || 'Unknown'}`,
                            short: true
                        },
                        {
                            title: 'Provider',
                            value: appointmentData.provider_name || `ID: ${appointmentData.provider_id || 'Unknown'}`,
                            short: true
                        },
                        {
                            title: 'Date & Time',
                            value: appointmentData.appointment_date && appointmentData.appointment_time
                                ? `${appointmentData.appointment_date} at ${appointmentData.appointment_time}`
                                : appointmentData.appointment_date || 'Not specified',
                            short: true
                        },
                        {
                            title: 'Status',
                            value: appointmentData.status || 'Pending',
                            short: true
                        },
                        {
                            title: 'Type',
                            value: appointmentData.type || 'General',
                            short: true
                        },
                        {
                            title: 'Appointment ID',
                            value: appointmentData.id,
                            short: true
                        },
                        {
                            title: 'Booked',
                            value: new Date(appointmentData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Appointment System',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send support request notifications
     */
    async notifySupportRequest(supportData) {
        const isProduction = this.environment === 'production';
        const priorityColors = {
            low: '#28a745',
            normal: '#17a2b8',
            high: '#ffc107',
            urgent: '#dc3545',
            critical: '#721c24'
        };
        const priorityEmojis = {
            low: '🟢',
            normal: '🔵',
            high: '🟡',
            urgent: '🟠',
            critical: '🔴'
        };
        const message = {
            text: `🆘 New Support Request ${isProduction ? '(PRODUCTION)' : '(STAGING)'}`,
            channel: isProduction ? '#support-requests' : '#support-requests-staging',
            username: 'JoinOmu Support Bot',
            icon_emoji: '🆘',
            attachments: [
                {
                    color: priorityColors[supportData.priority] || '#17a2b8',
                    title: `${priorityEmojis[supportData.priority] || '🔵'} ${supportData.subject}`,
                    text: supportData.message.length > 300
                        ? supportData.message.substring(0, 300) + '...'
                        : supportData.message,
                    fields: [
                        {
                            title: 'User',
                            value: supportData.user_name,
                            short: true
                        },
                        {
                            title: 'Email',
                            value: supportData.user_email,
                            short: true
                        },
                        {
                            title: 'User Type',
                            value: supportData.user_type.charAt(0).toUpperCase() + supportData.user_type.slice(1),
                            short: true
                        },
                        {
                            title: 'Priority',
                            value: supportData.priority.toUpperCase(),
                            short: true
                        },
                        {
                            title: 'Category',
                            value: supportData.category,
                            short: true
                        },
                        {
                            title: 'Request ID',
                            value: supportData.id,
                            short: true
                        },
                        {
                            title: 'Submitted',
                            value: new Date(supportData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Support System',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send feedback submission notifications
     */
    async notifyFeedbackSubmission(feedbackData) {
        const isProduction = this.environment === 'production';
        const typeEmojis = {
            general: '💬',
            bug_report: '🐛',
            feature_request: '💡',
            improvement: '⚡',
            complaint: '😞',
            compliment: '🎉'
        };
        const ratingEmoji = feedbackData.rating
            ? '⭐'.repeat(Math.min(feedbackData.rating, 5))
            : 'No rating';
        const message = {
            text: `💭 New Feedback Submission ${isProduction ? '(PRODUCTION)' : '(STAGING)'}`,
            channel: isProduction ? '#feedback' : '#feedback-staging',
            username: 'JoinOmu Feedback Bot',
            icon_emoji: '💭',
            attachments: [
                {
                    color: feedbackData.feature_request ? '#6f42c1' : '#17a2b8',
                    title: `${typeEmojis[feedbackData.feedback_type] || '💬'} ${feedbackData.feedback_type.replace('_', ' ').toUpperCase()} ${feedbackData.feature_request ? '(Feature Request)' : ''}`,
                    text: feedbackData.message.length > 300
                        ? feedbackData.message.substring(0, 300) + '...'
                        : feedbackData.message,
                    fields: [
                        {
                            title: 'User',
                            value: feedbackData.user_name,
                            short: true
                        },
                        {
                            title: 'Email',
                            value: feedbackData.user_email,
                            short: true
                        },
                        {
                            title: 'User Type',
                            value: feedbackData.user_type.charAt(0).toUpperCase() + feedbackData.user_type.slice(1),
                            short: true
                        },
                        {
                            title: 'Rating',
                            value: ratingEmoji,
                            short: true
                        },
                        {
                            title: 'Type',
                            value: feedbackData.feedback_type.replace('_', ' '),
                            short: true
                        },
                        {
                            title: 'Feedback ID',
                            value: feedbackData.id,
                            short: true
                        },
                        {
                            title: 'Submitted',
                            value: new Date(feedbackData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Feedback System',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send notification for medication adjustments by provider
     */
    async notifyMedicationAdjustments(adjustmentData) {
        const isProduction = this.environment === 'production';
        // Only send notifications in production
        if (!isProduction) {
            console.log('📢 [Staging] Medication adjustments notification skipped - production only');
            return { success: true };
        }
        const message = {
            text: `💊 Medication Adjustments Made (PRODUCTION)`,
            channel: '#medication-adjustments',
            username: 'JoinOmu Adjustments Bot',
            icon_emoji: '💊',
            attachments: [
                {
                    color: '#28a745',
                    title: 'Medication Adjustments Submitted',
                    text: adjustmentData.visit_summary ?
                        (adjustmentData.visit_summary.length > 200 ?
                            adjustmentData.visit_summary.substring(0, 200) + '...' :
                            adjustmentData.visit_summary) :
                        'Provider has made medication adjustments',
                    fields: [
                        {
                            title: 'Patient ID',
                            value: adjustmentData.patient_id,
                            short: true
                        },
                        {
                            title: 'Provider ID',
                            value: adjustmentData.provider_id,
                            short: true
                        },
                        {
                            title: 'Appointment ID',
                            value: adjustmentData.appointment_id,
                            short: true
                        },
                        {
                            title: 'Clinical Note ID',
                            value: adjustmentData.id,
                            short: true
                        },
                        {
                            title: 'Submitted',
                            value: new Date(adjustmentData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        },
                        ...(adjustmentData.medication_adjustments && adjustmentData.medication_adjustments.length > 0 ? [{
                                title: 'Medication Adjustments',
                                value: adjustmentData.medication_adjustments.map(med => {
                                    const medName = med.medication_name || 'Unknown Medication';
                                    const statusChange = med.previous_status && med.new_status ?
                                        ` (${med.previous_status} → ${med.new_status})` : '';
                                    const dosageChange = med.previous_dosage && med.new_dosage ?
                                        ` [${med.previous_dosage} → ${med.new_dosage}]` : '';
                                    return `• ${medName}${statusChange}${dosageChange}`;
                                }).join('\n'),
                                short: false
                            }] : [])
                    ],
                    footer: 'JoinOmu Medication Adjustments',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send notification for new medication preferences
     */
    async notifyNewMedicationPreference(preferenceData) {
        const isProduction = this.environment === 'production';
        // Only send notifications in production
        if (!isProduction) {
            console.log('📢 [Staging] New medication preference notification skipped - production only');
            return { success: true };
        }
        const sourceEmojis = {
            three_step_onboarding: '🎯',
            add_medication: '➕'
        };
        const sourceLabels = {
            three_step_onboarding: 'Three-Step Onboarding',
            add_medication: 'Add Medication Button'
        };
        const message = {
            text: `${sourceEmojis[preferenceData.source]} New Medication Preference Added (PRODUCTION)`,
            channel: '#medication-adjustments',
            username: 'JoinOmu Medication Bot',
            icon_emoji: sourceEmojis[preferenceData.source],
            attachments: [
                {
                    color: preferenceData.source === 'three_step_onboarding' ? '#28a745' : '#17a2b8',
                    title: `${preferenceData.medication_name} - ${sourceLabels[preferenceData.source]}`,
                    fields: [
                        {
                            title: 'Patient ID',
                            value: preferenceData.patient_id,
                            short: true
                        },
                        {
                            title: 'Medication',
                            value: preferenceData.medication_name,
                            short: true
                        },
                        {
                            title: 'Source',
                            value: sourceLabels[preferenceData.source],
                            short: true
                        },
                        {
                            title: 'Status',
                            value: preferenceData.status,
                            short: true
                        },
                        {
                            title: 'Next Due Date',
                            value: preferenceData.next_prescription_due || 'Not Set',
                            short: true
                        },
                        {
                            title: 'Added',
                            value: new Date(preferenceData.created_at).toLocaleString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                dateStyle: 'medium',
                                timeStyle: 'short'
                            }),
                            short: true
                        }
                    ],
                    footer: 'JoinOmu Medication Preferences',
                    ts: Math.floor(Date.now() / 1000)
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send appointment-related notifications
     */
    async notifyAppointmentEvent(event) {
        const colors = {
            scheduled: '#28a745',
            completed: '#007bff',
            cancelled: '#ffc107',
            no_show: '#dc3545'
        };
        const emojis = {
            scheduled: '📅',
            completed: '✅',
            cancelled: '❌',
            no_show: '😞'
        };
        const message = {
            text: `${emojis[event.type]} Appointment ${event.type}`,
            channel: '#appointments',
            attachments: [
                {
                    color: colors[event.type],
                    title: `Appointment ${event.type.replace('_', ' ')}`,
                    fields: [
                        {
                            title: 'Patient',
                            value: event.patientName,
                            short: true
                        },
                        {
                            title: 'Provider',
                            value: event.providerName,
                            short: true
                        },
                        {
                            title: 'Time',
                            value: event.appointmentTime,
                            short: true
                        },
                        ...(event.notes ? [{
                                title: 'Notes',
                                value: event.notes,
                                short: false
                            }] : [])
                    ]
                }
            ]
        };
        return await this.sendMessage(message);
    }
    /**
     * Send raw message to Slack
     */
    async sendMessage(message) {
        // If no webhook URLs or bot token, log locally instead
        if (!this.webhookUrl && !this.unifiedWebhookUrl && !this.botToken) {
            console.log('📢 [Slack Notification]:', JSON.stringify(message, null, 2));
            return { success: true };
        }
        // Debug: Log the target channel and environment
        console.log('📢 Sending Slack message to channel:', message.channel, 'Environment:', this.environment);
        const results = [];
        // Prefer Bot Token for channel-specific messages (supports dynamic channels)
        if (this.botToken && message.channel) {
            try {
                console.log('📢 Sending via Bot Token to channel:', message.channel);
                const result = await this.sendViaBotToken(message);
                results.push(result);
                console.log('✅ Bot Token message sent successfully');
            }
            catch (error) {
                console.error('❌ Bot Token message failed:', error);
                results.push({ success: false, error: error.message });
            }
        }
        else if (this.webhookUrl) {
            // Fallback to webhook if no bot token
            try {
                console.log('📢 Sending to channel-specific webhook ending with:', this.webhookUrl.slice(-10));
                const result = await this.sendToWebhook(this.webhookUrl, message);
                results.push(result);
                console.log('✅ Channel-specific webhook sent successfully');
            }
            catch (error) {
                console.error('❌ Channel-specific webhook failed:', error);
                results.push({ success: false, error: error.message });
            }
        }
        // ALSO send to all-activity feed using Bot Token
        if (this.botToken) {
            try {
                const allActivityMessage = {
                    ...message,
                    text: `📋 ${message.text}`, // Add prefix to identify all-activity feed
                    channel: '#all-activity'
                };
                console.log('📢 Sending to #all-activity via Bot Token');
                const result = await this.sendViaBotToken(allActivityMessage);
                results.push(result);
                console.log('✅ All-activity feed sent successfully');
            }
            catch (error) {
                console.error('❌ All-activity feed failed:', error);
                results.push({ success: false, error: error.message });
            }
        }
        else if (this.unifiedWebhookUrl) {
            // Fallback to old unified webhook if no bot token
            try {
                const unifiedMessage = {
                    ...message,
                    text: `📋 ${message.text}`, // Add prefix to identify unified feed
                    channel: undefined // Let original webhook use its configured channel
                };
                console.log('📢 Sending to unified feed webhook ending with:', this.unifiedWebhookUrl.slice(-10));
                const result = await this.sendToWebhook(this.unifiedWebhookUrl, unifiedMessage);
                results.push(result);
                console.log('✅ Unified feed webhook sent successfully');
            }
            catch (error) {
                console.error('❌ Unified feed webhook failed:', error);
                results.push({ success: false, error: error.message });
            }
        }
        // Return success if at least one webhook succeeded
        const anySuccess = results.some(r => r.success);
        return {
            success: anySuccess,
            error: anySuccess ? undefined : 'All delivery methods failed'
        };
    }
    /**
     * Helper method to send via Bot Token (supports dynamic channels)
     */
    async sendViaBotToken(message) {
        const response = await fetch('https://slack.com/api/chat.postMessage', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.botToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                channel: message.channel,
                text: message.text,
                username: message.username,
                icon_emoji: message.icon_emoji,
                attachments: message.attachments,
                blocks: message.blocks
            })
        });
        if (!response.ok) {
            throw new Error(`Slack API error: ${response.status} ${response.statusText}`);
        }
        const result = await response.json();
        if (result.ok) {
            return { success: true };
        }
        else {
            throw new Error(`Slack API error: ${result.error}`);
        }
    }
    /**
     * Helper method to send to a specific webhook URL
     */
    async sendToWebhook(webhookUrl, message) {
        const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(message)
        });
        if (!response.ok) {
            throw new Error(`Slack API error: ${response.status} ${response.statusText}`);
        }
        const result = await response.text();
        if (result === 'ok') {
            return { success: true };
        }
        else {
            throw new Error(`Slack webhook error: ${result}`);
        }
    }
    /**
     * Test Slack connectivity
     */
    async testConnection() {
        const testMessage = {
            text: '🧪 JoinOmu Slack Integration Test',
            channel: this.defaultChannel,
            username: 'JoinOmu Bot',
            icon_emoji: '🧪',
            attachments: [
                {
                    color: '#17a2b8',
                    title: 'Connection Test',
                    text: 'This is a test message to verify Slack integration is working correctly.',
                    fields: [
                        {
                            title: 'Environment',
                            value: this.environment.toUpperCase(),
                            short: true
                        },
                        {
                            title: 'Test Time',
                            value: new Date().toISOString(),
                            short: true
                        }
                    ]
                }
            ]
        };
        const result = await this.sendMessage(testMessage);
        return {
            success: result.success,
            message: result.success
                ? 'Slack integration test successful!'
                : `Slack integration test failed: ${result.error}`
        };
    }
}
// Export singleton instance
export const slackService = new SlackService();
